import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, formatTl } from '../lib/api';
import AraSec from '../ui/AraSec';
import { useAuth } from '../lib/auth';
import YeniSiparis from './YeniSiparis';
import FaturaCekmece from '../ui/FaturaCekmece';
import { ResmiRozet, type ResmiDurum } from '../ui/ResmiBelge';
import { sor } from '../ui/Sor';

type Kart = {
  id: number; no: string; title: string; status: string; statusLabel: string;
  cari: { id: number; name: string; phone: string | null };
  teklif: { id: number; no: string } | null;
  dueDate: string | null; gecikti: boolean; bekleyenGun: number; note: string | null;
  faturaKesildi?: boolean; invoicedAt?: string | null; resmi?: ResmiDurum;
  iptal?: boolean; iptalTarihi?: string | null; iptalNedeni?: string | null;
  depositKurus?: number | null; teklifTutarKurus?: number | null;
};
type Veri = {
  kolonlar: { id: string; ad: string; kartlar: Kart[] }[];
  iptaller?: Kart[];
  ozet: { acik: number; geciken: number };
};

/**
 * SİPARİŞ HATTI — hafif kanban (şartname 4.6).
 *
 * Sürükle-bırak HTML5 draggable ile; kütüphane eklemiyoruz (W3-CCD dışında
 * bağımlılık getirmemek için). Dokunmatik cihazda sürükleme güvenilir
 * olmadığından her kartta "ileri taşı" düğmesi de var — Mehmetcan telefondan
 * kullanacak.
 */
type Gorunum = 'pano' | 'liste';
const GORUNUM_ANAHTAR = 'am_siparis_gorunum';
/** Son seçilen görünüm; depolama okunamazsa (gizli sekme vb.) Pano. */
function gorunumOku(): Gorunum {
  try { return window.localStorage.getItem(GORUNUM_ANAHTAR) === 'liste' ? 'liste' : 'pano'; }
  catch { return 'pano'; }
}
/** Kolon başlığındaki şeridin tonu: erken aşamalar nötr, iş aşamaları vurgu. */
function panoTon(id: string): string {
  if (id === 'ONAY' || id === 'URETIM' || id === 'SEVK') return 'vurgu';
  if (id === 'TAHSILAT') return 'koyu';
  if (id === 'KAPANDI') return 'basari';
  return 'notr';
}

export default function Siparis() {
  const { can } = useAuth();
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  /** 'acik' = kapanmamışlar; ya da tek bir aşamanın kimliği; 'hepsi'. */
  const [suzgec, setSuzgec] = useState<string>('acik');
  const [gorunum, setGorunum] = useState<Gorunum>(gorunumOku);
  /** Pano: sürüklenen kartın kimliği ve üzerinde durulan kolon. */
  const [suruklenen, setSuruklenen] = useState<number | null>(null);
  /* Dar ekran: yatay kayan panoda o an görünen aşama (üstteki çiplerde işaretlenir). */
  const panoRef = useRef<HTMLDivElement>(null);
  const [panoAsama, setPanoAsama] = useState<string | null>(null);
  const panoKaydi = () => {
    const kap = panoRef.current;
    if (!kap) return;
    const sol = kap.getBoundingClientRect().left;
    let enYakin: string | null = null; let fark = Infinity;
    for (const k of kap.querySelectorAll<HTMLElement>('[data-asama]')) {
      const f = Math.abs(k.getBoundingClientRect().left - sol);
      if (f < fark) { fark = f; enYakin = k.dataset.asama ?? null; }
    }
    if (enYakin && enYakin !== panoAsama) setPanoAsama(enYakin);
  };
  const [hedef, setHedef] = useState<string | null>(null);
  function gorunumSec(g: Gorunum) {
    setGorunum(g);
    try { window.localStorage.setItem(GORUNUM_ANAHTAR, g); } catch { /* hatırlanmaz, sorun değil */ }
  }
  /** Faturalandır çekmecesi (ui/FaturaCekmece) hangi sipariş için açık. */
  const [faturaId, setFaturaId] = useState<number | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  /* Süzgeç: yazı (no/başlık/teklif/cari) ve tek cari. İkisi de boşsa her şey görünür. */
  const [ara, setAra] = useState('');
  const [cariSuz, setCariSuz] = useState('');
  const siparisCarileri = useMemo(() => {
    const m = new Map<number, string>();
    for (const k of (veri?.kolonlar ?? []).flatMap((x) => x.kartlar)) m.set(k.cari.id, k.cari.name);
    return [...m.entries()].map(([id, ad]) => ({ id, ad })).sort((x, y) => x.ad.localeCompare(y.ad, 'tr'));
  }, [veri]);
  const gor = useMemo(() => {
    if (!veri) return null;
    const q = ara.trim().toLocaleLowerCase('tr');
    const uyar = (k: Kart) => (!cariSuz || String(k.cari.id) === cariSuz)
      && (!q || `${k.no} ${k.title} ${k.cari.name} ${k.teklif?.no ?? ''}`.toLocaleLowerCase('tr').includes(q));
    return { ...veri, kolonlar: veri.kolonlar.map((kol) => ({ ...kol, kartlar: kol.kartlar.filter(uyar) })) };
  }, [veri, ara, cariSuz]);
  const [iptalAcik, setIptalAcik] = useState(false);
  /** İptal: neden zorunlu. Sipariş panodan düşer, "İptal edilenler"de nedeniyle durur. */
  async function iptalEt(k: Kart) {
    const neden = await sor({
      baslik: 'Siparişi iptal et', alt: k.no, aciklama: 'Sipariş panodan düşer; "İptal edilenler"de nedeniyle durur ve geri alınabilir.',
      etiket: 'İptal nedeni', yerTutucu: 'Müşteri vazgeçti, stok yok, fiyat…', zorunlu: true, azami: 300, onay: 'Siparişi iptal et', tehlike: true,
    });
    if (neden == null) return;
    setHata(null); setBilgi(null);
    try {
      const r = await api<{ uyari: string | null }>(`/api/siparis/${k.id}/iptal`, { method: 'POST', body: JSON.stringify({ neden }) });
      setBilgi(`${k.no} iptal edildi.${r.uyari ? ` ${r.uyari}` : ''}`); setIptalAcik(true);
      yukle();
    } catch (e) { setHata(e instanceof Error ? e.message : 'İptal edilemedi'); }
  }
  async function iptalGeriAl(k: Kart) {
    setHata(null); setBilgi(null);
    try { await api(`/api/siparis/${k.id}/iptal-geri-al`, { method: 'POST' }); setBilgi(`${k.no} yeniden açıldı.`); yukle(); }
    catch (e) { setHata(e instanceof Error ? e.message : 'Geri alınamadı'); }
  }

  const faturaAc = (id: number) => setFaturaId(id);

  const yukle = useCallback(() => {
    api<Veri>('/api/siparis').then(setVeri).catch((e) => setHata((e as Error).message));
  }, []);
  useEffect(yukle, [yukle]);

  async function tasi(id: number, status: string) {
    setHata(null);
    // İyimser güncelleme: kart hemen taşınsın, sunucu arkadan onaylasın.
    setVeri((v) => v && {
      ...v,
      kolonlar: v.kolonlar.map((k) => ({
        ...k,
        kartlar: k.kartlar.filter((c) => c.id !== id),
      })).map((k) => (k.id === status
        ? {
            ...k,
            kartlar: [
              ...k.kartlar,
              ...v.kolonlar.flatMap((x) => x.kartlar).filter((c) => c.id === id)
                .map((c) => ({ ...c, status })),
            ],
          }
        : k)),
    });
    try {
      const r = await api<{ uyari?: string | null }>(`/api/siparis/${id}/durum`, { method: 'POST', body: JSON.stringify({ status }) });
      // Sevk öncesi ödeme alınmadıysa geçiş olur ama kullanıcı uyarılır.
      setBilgi(r.uyari ?? null);
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Taşınamadı');
      yukle();
    }
  }

  if (hata && !veri) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon">!</span><span>{hata}</span>
      </div>
    );
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Sipariş Hattı</h1>
          <div className="ccd-meta">
            {veri ? `${veri.ozet.acik} açık sipariş` : '…'}
            {veri && veri.ozet.geciken > 0 && (
              <span className="ccd-kpi-down"> · {veri.ozet.geciken} termin geçti</span>
            )}
            {' '}· {gorunum === 'pano' ? 'kartı sürükleyin ya da oklarla ilerletin' : 'aşamaya tıklayın ya da düğmeyle ilerletin'}
          </div>
        </div>
        <div className="ccd-row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <div className="ccd-pano-secici" role="group" aria-label="Görünüm">
            <button type="button" aria-pressed={gorunum === 'pano'} title="Aşamaları kolon kolon göster" onClick={() => gorunumSec('pano')}>Pano</button>
            <button type="button" aria-pressed={gorunum === 'liste'} title="Siparişleri alt alta listele" onClick={() => gorunumSec('liste')}>Liste</button>
          </div>
          {can('order.write') && (
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setYeniAcik(true)} data-tur="yeni-siparis">
              + Yeni Sipariş
            </button>
          )}
        </div>
      </div>

      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon">!</span><span>{hata}</span>
        </div>
      )}

      {/* Süzgeç: tek siparişi ya da tek carinin siparişlerini göster; boşken hepsi görünür. */}
      {veri && (
        <div className="ccd-row" style={{ flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div className="ccd-search" style={{ flex: '1 1 240px', maxWidth: 340 }}>
            <span className="ccd-search-icon" aria-hidden="true">⌕</span>
            <input className="ccd-input" type="search" inputMode="search" enterKeyHint="search" autoComplete="off" placeholder="Sipariş no, başlık ya da teklif ara…" aria-label="Siparişlerde ara"
                   value={ara} onChange={(e) => setAra(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 240px', maxWidth: 340 }}>
            <AraSec deger={cariSuz} degis={setCariSuz} yerTutucu="Cari seç: yalnız onun siparişleri…" bosEtiket="— tüm cariler —"
                    secenekler={siparisCarileri} />
          </div>
          {(ara || cariSuz) && (
            <>
              <span className="ccd-meta">{gor ? gor.kolonlar.reduce((n, k) => n + k.kartlar.length, 0) : 0} sipariş gösteriliyor</span>
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => { setAra(''); setCariSuz(''); }}>Süzgeci temizle</button>
            </>
          )}
        </div>
      )}

      {!veri || !gor ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : gorunum === 'pano' ? (
        /* PANO: yedi aşama yan yana. Kart kolona sürüklenir; dokunmatikte oklar kullanılır.
           Dar ekranda kolonlar yatay kayar ve yapışarak durur; üstteki çipler aşamaya götürür. */
        <>
        <div className="ccd-pano-cipler" role="tablist" aria-label="Aşamaya git">
          {gor.kolonlar.map((kol) => (
            <button type="button" key={kol.id} role="tab" aria-selected={panoAsama === kol.id} className={`ccd-sa-cip${panoAsama === kol.id ? ' ccd-is-active' : ''}`}
                    onClick={() => {
                      setPanoAsama(kol.id);
                      // `scrollIntoView` yapışmalı (scroll-snap) kapta yarıda kalıyor; hedef konum doğrudan verilir.
                      const kap = panoRef.current; const hedefKolon = kap?.querySelector<HTMLElement>(`[data-asama="${kol.id}"]`);
                      if (kap && hedefKolon) kap.scrollTo({ left: hedefKolon.offsetLeft - kap.offsetLeft, behavior: 'smooth' });
                    }}>
              {kol.ad}<span className="ccd-sa-cip-sayi">{kol.kartlar.length}</span>
            </button>
          ))}
        </div>
        <div className={`ccd-pano${suruklenen != null ? ' ccd-pano-surukleniyor' : ''}`} ref={panoRef} onScroll={panoKaydi}>
          {gor.kolonlar.map((kol, sira) => {
            const onceki = gor.kolonlar[sira - 1];
            const sonraki = gor.kolonlar[sira + 1];
            const yazar = can('order.write');
            return (
              <section key={kol.id} className={`ccd-pano-kolon${hedef === kol.id ? ' ccd-pano-hedef' : ''}${kol.kartlar.length === 0 ? ' ccd-pano-kolon-bos' : ''}`} data-ton={panoTon(kol.id)} data-asama={kol.id}
                       aria-label={`${kol.ad} aşaması, ${kol.kartlar.length} sipariş`}
                       onDragOver={(e) => { if (suruklenen == null) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (hedef !== kol.id) setHedef(kol.id); }}
                       onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHedef((h) => (h === kol.id ? null : h)); }}
                       onDrop={(e) => {
                         e.preventDefault();
                         const id = suruklenen;
                         setSuruklenen(null); setHedef(null);
                         // Kendi kolonuna bırakılan kart taşınmaz.
                         if (id != null && !kol.kartlar.some((c) => c.id === id)) void tasi(id, kol.id);
                       }}>
                <header className="ccd-pano-baslik" data-fihrist="siparis-asamalari">
                  <span className="ccd-pano-ad">{kol.ad}</span>
                  <span className="ccd-pano-adet">{kol.kartlar.length}</span>
                  {kol.kartlar.some((x) => x.teklifTutarKurus != null) && (
                    <span className="ccd-pano-tutar" data-fihrist="siparis-tutari" title="Bu aşamadaki siparişlerin teklif toplamı (KDV dahil). Sağ tıklayınca: nasıl hesaplanır?">{formatTl(kol.kartlar.reduce((t, x) => t + (x.teklifTutarKurus ?? 0), 0))}</span>
                  )}
                </header>
                <div className="ccd-pano-govde">
                  {kol.kartlar.length === 0 ? (
                    <div className="ccd-pano-bos">Sipariş yok</div>
                  ) : kol.kartlar.map((k) => (
                    <article key={k.id} draggable={yazar}
                             className={`ccd-pano-kart${k.gecikti ? ' ccd-pano-gec' : ''}${suruklenen === k.id ? ' ccd-pano-tutulan' : ''}`}
                             onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(k.id)); setSuruklenen(k.id); }}
                             onDragEnd={() => { setSuruklenen(null); setHedef(null); }}>
                      <div className="ccd-pano-ust">
                        <Link to={`/siparis/${k.id}`} className="ccd-mono ccd-pano-no" draggable={false} title="Sipariş detayını aç">{k.no}</Link>
                        {k.faturaKesildi && (
                          <span className="ccd-pano-rozet" title={k.invoicedAt ? `Faturalandı · ${formatTarih(k.invoicedAt)}` : 'Faturalandı'}>faturalandı</span>
                        )}
                        <ResmiRozet r={k.resmi} kisa />
                      </div>
                      <Link to={`/siparis/${k.id}`} className="ccd-pano-konu ccd-sdetay-bag" draggable={false} title={`${k.title} — sipariş detayını aç`}>{k.title}</Link>
                      <Link to={`/crm/${k.cari.id}`} className="ccd-pano-cari" draggable={false}>{k.cari.name}</Link>
                      {(k.teklif || k.teklifTutarKurus != null) && (
                        <div className="ccd-pano-satir">
                          {k.teklif && <Link to={`/teklif/${k.teklif.id}`} className="ccd-mono" draggable={false}>{k.teklif.no}</Link>}
                          {k.teklifTutarKurus != null && <b>{formatTl(k.teklifTutarKurus)}</b>}
                        </div>
                      )}
                      {k.dueDate && (
                        <div className={`ccd-pano-satir${k.gecikti ? ' ccd-pano-kirmizi' : ''}`}>
                          <span>Termin {formatTarih(k.dueDate)}{k.gecikti ? ' (geçti)' : ''}</span>
                        </div>
                      )}
                      {k.bekleyenGun > 7 && kol.id !== 'KAPANDI' && (
                        <div className="ccd-pano-satir"><span>{k.bekleyenGun} gündür bu aşamada</span></div>
                      )}
                      {!k.faturaKesildi && k.teklif && can('settings.write') && (
                        <button type="button" className="ccd-btn ccd-btn-sm ccd-pano-fatura" onClick={() => void faturaAc(k.id)}
                                title="Teklifteki satırlarla Paraşüt'te satış faturası açar">Faturalandır</button>
                      )}
                      <div className="ccd-pano-alt">
                        {/* İlerleme: geçilen aşamalar dolu, şimdiki vurgulu. Yalnız görsel. */}
                        <span className="ccd-pano-ilerleme" role="img" aria-label={`${gor.kolonlar.length} aşamanın ${sira + 1}. aşaması: ${kol.ad}`}>
                          {gor.kolonlar.map((a, n) => (
                            <i key={a.id} className={n < sira ? 'ccd-pano-gecti' : n === sira ? 'ccd-pano-simdi' : ''} />
                          ))}
                        </span>
                        {yazar && (
                          <span className="ccd-pano-oklar">
                            {onceki && (
                              <button type="button" title={`${onceki.ad} aşamasına geri al`} aria-label={`${k.no}: ${onceki.ad} aşamasına geri al`}
                                      onClick={() => void tasi(k.id, onceki.id)}>←</button>
                            )}
                            {sonraki && (
                              <button type="button" title={`${sonraki.ad} aşamasına geçir`} aria-label={`${k.no}: ${sonraki.ad} aşamasına geçir`}
                                      onClick={() => void tasi(k.id, sonraki.id)}>→</button>
                            )}
                            {kol.id !== 'KAPANDI' && (
                              <button type="button" title="Siparişi iptal et (neden sorulur)" aria-label={`${k.no}: siparişi iptal et`}
                                      onClick={() => void iptalEt(k)}>×</button>
                            )}
                          </span>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        </>
      ) : (() => {
        const asamalar = gor.kolonlar.map((k) => ({ id: k.id, ad: k.ad }));
        const hepsi = gor.kolonlar.flatMap((k) => k.kartlar);
        const liste = hepsi
          .filter((k) => (suzgec === 'acik' ? k.status !== 'KAPANDI' : suzgec === 'hepsi' ? true : k.status === suzgec))
          .sort((x, y) => asamalar.findIndex((a) => a.id === x.status) - asamalar.findIndex((a) => a.id === y.status));
        return (
          <>
            {/* Aşama özeti: sayıya tıklayınca o aşamadaki siparişler listelenir. */}
            <div className="ccd-asama-ozet" role="tablist" aria-label="Aşamaya göre süz">
              <button role="tab" aria-selected={suzgec === 'acik'} className="ccd-asama-kutu" onClick={() => setSuzgec('acik')}>
                <b>{hepsi.filter((k) => k.status !== 'KAPANDI').length}</b><span>Açık siparişler</span></button>
              {gor.kolonlar.map((k) => (
                <button key={k.id} role="tab" aria-selected={suzgec === k.id} className="ccd-asama-kutu" onClick={() => setSuzgec(k.id)}>
                  <b>{k.kartlar.length}</b><span>{k.ad}</span></button>
              ))}
            </div>

            {liste.length === 0 ? (
              <div className="ccd-empty">Bu aşamada sipariş yok.</div>
            ) : liste.map((k) => {
              const sira = asamalar.findIndex((a) => a.id === k.status);
              const sonraki = asamalar[sira + 1];
              const onceki = asamalar[sira - 1];
              return (
                <div key={k.id} className={`ccd-card ccd-stack-sm${k.gecikti ? ' ccd-siparis-gec' : ''}`}>
                  <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <Link to={`/siparis/${k.id}`} className="ccd-sdetay-bag" title="Sipariş detayını aç">
                        <span className="ccd-mono" style={{ fontWeight: 700 }}>{k.no}</span>{' '}
                        <b>{k.title}</b>
                      </Link>
                      <div className="ccd-meta">
                        <Link to={`/crm/${k.cari.id}`}>{k.cari.name}</Link>
                        {k.teklif && <> · teklif <Link to={`/teklif/${k.teklif.id}`} className="ccd-mono">{k.teklif.no}</Link></>}
                        {k.teklifTutarKurus != null && ` · ${formatTl(k.teklifTutarKurus)}`}
                        {k.dueDate && <span className={k.gecikti ? 'ccd-kpi-down' : ''}> · termin {formatTarih(k.dueDate)}{k.gecikti ? ' (geçti)' : ''}</span>}
                        {k.bekleyenGun > 7 && k.status !== 'KAPANDI' && ` · ${k.bekleyenGun} gündür bu aşamada`}
                      </div>
                    </div>
                    <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      {k.faturaKesildi ? (
                        <><span className="ccd-badge ccd-badge-success">faturalandı{k.invoicedAt ? ` · ${formatTarih(k.invoicedAt)}` : ''}</span><ResmiRozet r={k.resmi} kisa /></>
                      ) : k.teklif && can('settings.write') && (
                        <button className="ccd-btn ccd-btn-sm" onClick={() => void faturaAc(k.id)}
                                title="Teklifteki satırlarla Paraşüt'te satış faturası açar">Faturalandır</button>
                      )}
                      {can('order.write') && k.status !== 'KAPANDI' && (
                        <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ color: 'var(--ccd-danger)' }}
                                title="Siparişi iptal et (neden sorulur)" onClick={() => void iptalEt(k)}>İptal et</button>
                      )}
                      {can('order.write') && onceki && (
                        <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" title={`${onceki.ad} aşamasına geri al`}
                                onClick={() => void tasi(k.id, onceki.id)}>← Geri al</button>
                      )}
                      {can('order.write') && sonraki && (
                        <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => void tasi(k.id, sonraki.id)}>
                          {sonraki.ad} aşamasına geçir →</button>
                      )}
                    </div>
                  </div>
                  {/* Süreç çubuğu: geçilen aşamalar dolu, şimdiki vurgulu. Aşamaya tıklayınca oraya taşınır. */}
                  <ol className="ccd-surec" aria-label={`${k.no} süreç aşaması`}>
                    {asamalar.map((a, n) => (
                      <li key={a.id} className={n < sira ? 'ccd-surec-gecti' : n === sira ? 'ccd-surec-simdi' : ''}>
                        <button type="button" disabled={!can('order.write') || n === sira}
                                title={n === sira ? 'Şu anki aşama' : `${a.ad} aşamasına taşı`}
                                onClick={() => { if (window.confirm(`${k.no} "${a.ad}" aşamasına taşınsın mı?`)) void tasi(k.id, a.id); }}>
                          <span className="ccd-surec-nokta">{n < sira ? '✓' : n + 1}</span>
                          <span className="ccd-surec-ad">{a.ad}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </>
        );
      })()}

      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}

      {veri && (veri.iptaller?.length ?? 0) > 0 && (
        <div className="ccd-card ccd-stack-sm">
          <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ alignSelf: 'flex-start' }}
                  aria-expanded={iptalAcik} onClick={() => setIptalAcik(!iptalAcik)}>
            {iptalAcik ? '▾' : '▸'} İptal edilenler ({veri.iptaller!.length})
          </button>
          {iptalAcik && veri.iptaller!.map((k) => (
            <div key={k.id} className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <Link to={`/siparis/${k.id}`} className="ccd-sdetay-bag" title="Sipariş detayını aç"><span className="ccd-mono" style={{ fontWeight: 700 }}>{k.no}</span> <s>{k.title}</s></Link>
                <div className="ccd-meta">
                  <Link to={`/crm/${k.cari.id}`}>{k.cari.name}</Link>
                  {k.teklif && <> · teklif <Link to={`/teklif/${k.teklif.id}`} className="ccd-mono">{k.teklif.no}</Link></>}
                  {k.iptalTarihi && ` · ${formatTarih(k.iptalTarihi)} tarihinde iptal`} · {k.statusLabel} aşamasındaydı
                  {k.faturaKesildi && <span className="ccd-kpi-down"> · Paraşüt'te faturası var, orada ayrıca iptal edilmeli</span>}
                </div>
                {k.iptalNedeni && <div className="ccd-meta"><b>Neden:</b> {k.iptalNedeni}</div>}
              </div>
              {can('order.write') && (
                <button className="ccd-btn ccd-btn-sm" onClick={() => void iptalGeriAl(k)} title="Siparişi kaldığı aşamada yeniden açar">İptali geri al</button>
              )}
            </div>
          ))}
        </div>
      )}

      {faturaId != null && <FaturaCekmece siparisId={faturaId} kapat={() => setFaturaId(null)} kesildi={yukle} />}

      {yeniAcik && (
        <YeniSiparis kapat={() => setYeniAcik(false)} eklendi={() => { setYeniAcik(false); yukle(); }} />
      )}
    </>
  );
}
